import {
  usePrimaryControlsStripHasBackControl,
  usePrimaryControlsStripLeadingIsOverridden,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { useSetIsPrimaryControlsStripFadeHidden } from "@-/dev.improve.haniverse.routes.nav._context";
import {
  usePrimaryControlsStripToolbarFloating,
  usePrimaryControlsStripToolbarSurface,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { ScrollEdgeViewport } from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import {
  requestLearnCards,
  usePrimaryNavigationStripOrder,
  usePrimaryNavigationStripScreens,
} from "@-/dev.improve.haniverse.routes.nav._lib.primary-navigation-strip";
import {
  PrimaryNavigationLayout,
  type PrimaryNavigationLayoutHandle,
} from "@-/dev.improve.haniverse.routes.nav.views.primary-navigation";
import type { RoutePath } from "@-/com.npmjs.expo-router";
import { Slot, usePathname } from "@-/com.npmjs.expo-router";
import { Platform, StyleSheet, View } from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import {
  SpaceBackdrop,
  SpaceStatusBar,
} from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { useEffect, useRef } from "react";
import { useOnboardingProgressSync } from "@-/dev.improve.haniverse.lib.onboarding";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";
import {
  OnboardingIntroOverlay,
  OnboardingSheet,
  useOnboardingIntroOverlayGate,
  useOnboardingLearnMap,
  useOnboardingPlaceReactions,
  useOnboardingSheetGate,
} from "@-/dev.improve.haniverse.routes.nav.app.onboarding";
import { ToolbarLeadingSlotFilledContext } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  PrimaryNavigationStripSurfaceProvider,
  usePrimaryNavigationStripSurfaceColorVariable,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";

const appBase = "/(nav)/(app)" satisfies RoutePath;

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
});

/**
 * The native app shell: the navigation chrome the account drawer slides over.
 *
 * The shared nav layout owns the drawer so it survives navigation from the app to auth.
 */
function AppShell() {
  const insets = useSafeAreaInsets();
  const primaryNavigationStripScreens = usePrimaryNavigationStripScreens();
  const primaryNavigationStripOrder = usePrimaryNavigationStripOrder();
  const pathname = usePathname();
  const isSlideLessonRoute = pathname.startsWith("/learn/lesson/slide");
  // The focused scene paints every system edge; safe-area clearance moves content only.
  const toolbarSurface = usePrimaryControlsStripToolbarSurface();
  const sceneSurface = usePrimaryNavigationStripSurfaceColorVariable();
  const backgroundClassName =
    sceneSurface === "--color-surface" || toolbarSurface === "surface"
      ? "bg-surface"
      : sceneSurface === "--color-background" || toolbarSurface === "page"
        ? "bg-background"
        : "bg-muted";
  // A glass bar floating over its page scroller (iOS 26): that scroller reaches up under the status
  // bar, so the band shows the page itself and UIKit blurs it with the bar.
  const isToolbarFloating = usePrimaryControlsStripToolbarFloating();
  const safeAreaSurfaceClassName = backgroundClassName;
  // A new learner's onboarding (`lib/onboarding`): the globe and its questions over everything,
  // then the sheet above the strip, which reacts to where the learner goes and what they learn by
  // introducing each destination the first time they open it and unlocking the next.
  useOnboardingProgressSync();
  useOnboardingPlaceReactions();
  useOnboardingLearnMap();
  const onboardingIntroOverlayGate = useOnboardingIntroOverlayGate();
  const onboardingSheetGate = useOnboardingSheetGate();
  const primaryNavigationRef = useRef<PrimaryNavigationLayoutHandle>(null);

  return (
    <View className={`flex-1 ${backgroundClassName}`} style={styles.root}>
      {backgroundClassName === "bg-muted" ? <SpaceBackdrop /> : null}
      <ScrollEdgeViewport
        edgeToEdge={isToolbarFloating || isSlideLessonRoute}
        topInset={isSlideLessonRoute ? 0 : insets.top}
      >
        {/* Pad only the sides here; the primary navigation strip owns home-indicator/navigation-button
          clearance. Context insets (not a native `SafeAreaView`) so landscape clears only the
          edge holding the sensor housing and the rail reaches the unobstructed edge. */}
        <View className="flex-1" style={{ paddingLeft: insets.left, paddingRight: insets.right }}>
          <PrimaryNavigationLayout
            base={appBase}
            ref={primaryNavigationRef}
            screens={primaryNavigationStripScreens}
            stripOrder={primaryNavigationStripOrder}
          />
        </View>
      </ScrollEdgeViewport>
      {/* Keep safe-area chrome inside the moving shell, beneath the drawer's full-height scrim. */}
      {isToolbarFloating ? null : (
        <View
          className={`absolute inset-x-0 top-0 z-50 ${safeAreaSurfaceClassName}`}
          pointerEvents="none"
          style={{ height: insets.top }}
        >
          {safeAreaSurfaceClassName === "bg-muted" ? (
            <>
              <SpaceBackdrop />
              <SpaceStatusBar />
            </>
          ) : null}
        </View>
      )}
      {onboardingSheetGate !== null && (
        <OnboardingSheet
          gate={onboardingSheetGate}
          switchTab={(screenName) => {
            primaryNavigationRef.current?.switchTab(screenName);
            if (screenName === "learn") {
              // Learn is still retaining the map behind Browse when the onboarding unlocks it.
              // `switchTab` focuses that scene directly (there is no tab-press event), so ask the
              // now-focused map to reveal cards once its focus effect has subscribed. Without this,
              // "Go to Learn" strands a first-time learner on the map with no sheet or CTA.
              safeRequestAnimationFrame(() => requestLearnCards(), "onboarding-go-to-learn", {
                frames: 2,
              });
            }
          }}
        />
      )}
      {onboardingIntroOverlayGate !== null && (
        <OnboardingIntroOverlay gate={onboardingIntroOverlayGate} />
      )}
    </View>
  );
}

export function AppLayoutContent() {
  const setIsPrimaryControlsStripFadeHidden = useSetIsPrimaryControlsStripFadeHidden();
  const hasAccountDrawerBackControl = usePrimaryControlsStripHasBackControl({
    appShellPlacement: "account-drawer",
  });
  const isLeadingOverridden = usePrimaryControlsStripLeadingIsOverridden();

  useEffect(
    () => () => {
      setIsPrimaryControlsStripFadeHidden(false);
    },
    [setIsPrimaryControlsStripFadeHidden],
  );

  // Web has no in-app navigation chrome at all: the dictionary-style tab header rendered by
  // `NavLayoutContent` is the only navigation surface, and the active `(app)` child renders
  // straight through. URL-driven `<Slot />` navigation (no drawer/tab navigator) also keeps
  // the server-rendered HTML free of app-shell scaffolding.
  if (Platform.OS === "web") {
    return <Slot />;
  }

  return (
    <ToolbarLeadingSlotFilledContext value={hasAccountDrawerBackControl || isLeadingOverridden}>
      <PrimaryNavigationStripSurfaceProvider>
        <AppShell />
      </PrimaryNavigationStripSurfaceProvider>
    </ToolbarLeadingSlotFilledContext>
  );
}
